import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Sparkles, Loader2, Copy, LogOut, ArrowLeft, Linkedin, Megaphone,
  Mail, Share2, Printer, Lock, Inbox, Building2, Phone, BellRing, X,
  Layers, Wand2,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Logo } from "@/components/Logo";
import { LaunchKit } from "@/components/LaunchKit";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTENT_TYPES = [
  { id: "campaign_pack", label: "All Platforms", icon: Layers },
  { id: "linkedin_post", label: "LinkedIn Post", icon: Linkedin },
  { id: "google_ads", label: "Google Ads Copy", icon: Megaphone },
  { id: "email_outreach", label: "Email Outreach", icon: Mail },
  { id: "social_post", label: "Social Post", icon: Share2 },
  { id: "flyer_copy", label: "Flyer Copy", icon: Printer },
];

const TONES = ["Professional", "Warm & Friendly", "Bold & Punchy", "Empathetic", "Data-Driven"];

const formatDetail = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return "Something went wrong.";
};

export default function MarketingStudio() {
  const [user, setUser] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginBusy, setLoginBusy] = useState(false);

  const [contentType, setContentType] = useState("linkedin_post");
  const [goal, setGoal] = useState("");
  const [tone, setTone] = useState("Professional");
  const [output, setOutput] = useState("");
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState("studio");
  const [enquiries, setEnquiries] = useState([]);
  const [alert, setAlert] = useState(null);
  const outputRef = useRef(null);
  const knownIdsRef = useRef(null);

  const beep = () => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = new Ctx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = 880;
      gain.gain.setValueAtTime(0.18, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.7);
      osc.start();
      osc.stop(ctx.currentTime + 0.7);
    } catch {}
  };

  const loadHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/marketing/history`, { withCredentials: true });
      setHistory(data);
    } catch {}
  };

  const loadEnquiries = async () => {
    try {
      const { data } = await axios.get(`${API}/enquiries`, { withCredentials: true });
      const sorted = [...data].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      if (knownIdsRef.current !== null) {
        const fresh = sorted.filter((e) => !knownIdsRef.current.has(e.id));
        if (fresh.length > 0) {
          setAlert(fresh[0]);
          toast.success(`New enquiry from ${fresh[0].full_name}`);
          beep();
        }
      }
      knownIdsRef.current = new Set(sorted.map((e) => e.id));
      setEnquiries(sorted);
    } catch {}
  };

  const suggestGoal = async () => {
    try {
      const { data } = await axios.get(`${API}/marketing/goal-suggestion`, { withCredentials: true });
      setGoal(data.goal);
    } catch {}
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((r) => {
        setUser(r.data);
        loadHistory();
        loadEnquiries();
        suggestGoal();
      })
      .catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    const timer = setInterval(loadEnquiries, 15000);
    return () => clearInterval(timer);
  }, [user]);

  useEffect(() => {
    outputRef.current?.scrollTo({ top: outputRef.current.scrollHeight });
  }, [output]);

  const login = async (e) => {
    e.preventDefault();
    setLoginBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, loginForm, { withCredentials: true });
      setUser(data);
      toast.success(`Welcome back, ${data.name}`);
      loadHistory();
      loadEnquiries();
      suggestGoal();
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail));
    } finally {
      setLoginBusy(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(false);
    setHistory([]);
    setOutput("");
  };

  const generate = async () => {
    if (goal.trim().length < 3) {
      toast.error("Describe your campaign goal first.");
      return;
    }
    setGenerating(true);
    setOutput("");
    try {
      const resp = await fetch(`${API}/marketing/generate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ content_type: contentType, goal, tone }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(formatDetail(data.detail));
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          if (!part.startsWith("data: ")) continue;
          const payload = JSON.parse(part.slice(6));
          if (payload.delta) setOutput((o) => o + payload.delta);
          if (payload.error) toast.error(payload.error);
          if (payload.done) loadHistory();
        }
      }
    } catch (err) {
      toast.error(err.message || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const copy = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  return (
    <div className="min-h-screen bg-[#0A261E]" data-testid="marketing-studio-page">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" data-testid="studio-back-link" className="text-[#A3B8B0] hover:text-[#F5F7F6] transition-colors">
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <Logo dark />
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-[#EAB308]/30 bg-[#EAB308]/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#EAB308]">
              <Sparkles className="h-3 w-3" /> AI Marketing Studio
            </span>
            {user && (
              <button
                onClick={logout}
                data-testid="studio-logout-btn"
                className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-[#F5F7F6] hover:bg-white/10 transition-colors"
              >
                <LogOut className="h-3.5 w-3.5" /> Log out
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {user === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-[#EAB308]" />
          </div>
        ) : user === false ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-md rounded-3xl border border-white/10 bg-[#123328] p-8 sm:p-10"
            data-testid="studio-login-card"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#EAB308]/15">
              <Lock className="h-5 w-5 text-[#EAB308]" />
            </div>
            <h1 className="mt-6 font-display text-2xl font-bold text-[#F5F7F6]">Owner access only</h1>
            <p className="mt-2 text-sm leading-relaxed text-[#A3B8B0]">
              Sign in to use the AI marketing assistant for Verma Med Associates.
            </p>
            <form onSubmit={login} className="mt-8 space-y-4" data-testid="studio-login-form">
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#F5F7F6]" htmlFor="studio-email">Email</label>
                <Input
                  id="studio-email"
                  type="email"
                  data-testid="studio-email-input"
                  className="border-white/15 bg-[#0A261E] text-[#F5F7F6] placeholder:text-[#A3B8B0]/50"
                  placeholder="admin@vermamedassociates.com"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))}
                  required
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-[#F5F7F6]" htmlFor="studio-password">Password</label>
                <Input
                  id="studio-password"
                  type="password"
                  data-testid="studio-password-input"
                  className="border-white/15 bg-[#0A261E] text-[#F5F7F6]"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                  required
                />
              </div>
              <button
                type="submit"
                disabled={loginBusy}
                data-testid="studio-login-submit-btn"
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#EAB308] px-6 py-3 text-sm font-bold text-[#0A261E] transition-all hover:brightness-110 disabled:opacity-60"
              >
                {loginBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Sign In to Studio
              </button>
            </form>
          </motion.div>
        ) : (
          <>
            {alert && (
              <motion.div
                initial={{ opacity: 0, y: -16, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.35 }}
                className="mb-8 rounded-3xl border-2 border-[#EAB308] bg-[#EAB308] p-6 shadow-2xl"
                data-testid="new-enquiry-alert"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#0A261E]">
                      <BellRing className="h-5 w-5 text-[#EAB308]" />
                    </div>
                    <div>
                      <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.25em] text-[#0A261E]/70">
                        New enquiry just arrived
                      </p>
                      <p className="mt-1 font-display text-xl font-bold text-[#0A261E]" data-testid="alert-enquiry-name">
                        {alert.full_name}
                        {alert.practice_name ? ` · ${alert.practice_name}` : ""}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0A261E] px-3 py-1 font-mono text-[11px] text-[#F5F7F6]">
                          <Mail className="h-3 w-3 text-[#EAB308]" /> {alert.email}
                        </span>
                        {alert.phone && (
                          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#0A261E] px-3 py-1 font-mono text-[11px] text-[#F5F7F6]">
                            <Phone className="h-3 w-3 text-[#EAB308]" /> {alert.phone}
                          </span>
                        )}
                        {alert.specialty && (
                          <span className="rounded-full bg-[#0A261E]/15 px-3 py-1 font-mono text-[11px] text-[#0A261E]">{alert.specialty}</span>
                        )}
                      </div>
                      {alert.message && (
                        <p className="mt-2.5 max-w-xl text-sm font-medium leading-relaxed text-[#0A261E]" data-testid="alert-enquiry-message">
                          "{alert.message.length > 160 ? alert.message.slice(0, 160) + "…" : alert.message}"
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => setAlert(null)}
                    data-testid="alert-dismiss-btn"
                    aria-label="Dismiss alert"
                    className="shrink-0 rounded-full p-1.5 text-[#0A261E]/70 transition-colors hover:bg-[#0A261E]/10"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <button
                  onClick={() => { setTab("enquiries"); setAlert(null); }}
                  data-testid="alert-view-btn"
                  className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0A261E] px-5 py-2.5 text-xs font-bold text-[#EAB308] transition-all hover:brightness-125"
                >
                  <Inbox className="h-3.5 w-3.5" /> Open in Enquiries
                </button>
              </motion.div>
            )}
            <div className="mb-8 inline-flex rounded-full border border-white/15 bg-white/5 p-1" data-testid="studio-tabs">
              <button
                data-testid="studio-tab-content"
                onClick={() => setTab("studio")}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                  tab === "studio" ? "bg-[#EAB308] text-[#0A261E]" : "text-[#A3B8B0] hover:text-[#F5F7F6]"
                }`}
              >
                <Sparkles className="h-4 w-4" /> Content Studio
              </button>
              <button
                data-testid="studio-tab-enquiries"
                onClick={() => { setTab("enquiries"); loadEnquiries(); }}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
                  tab === "enquiries" ? "bg-[#EAB308] text-[#0A261E]" : "text-[#A3B8B0] hover:text-[#F5F7F6]"
                }`}
              >
                <Inbox className="h-4 w-4" /> Enquiries
                {enquiries.length > 0 && (
                  <span
                    className={`rounded-full px-2 py-0.5 font-mono text-[10px] ${
                      tab === "enquiries" ? "bg-[#0A261E] text-[#EAB308]" : "bg-[#EAB308]/15 text-[#EAB308]"
                    }`}
                    data-testid="enquiries-count-badge"
                  >
                    {enquiries.length}
                  </span>
                )}
              </button>
            </div>

            {tab === "enquiries" ? (
              <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} data-testid="enquiries-panel">
                <h1 className="font-display text-2xl font-bold text-[#F5F7F6]">Consultation enquiries</h1>
                <p className="mt-2 text-sm text-[#A3B8B0]">
                  Every website enquiry, newest first — also emailed to vermamedassociates@gmail.com.
                </p>
                {enquiries.length === 0 ? (
                  <div className="mt-10 rounded-3xl border border-white/10 bg-[#123328] p-12 text-center" data-testid="enquiries-empty">
                    <Inbox className="mx-auto h-8 w-8 text-[#A3B8B0]/50" />
                    <p className="mt-4 text-sm text-[#A3B8B0]">No enquiries yet. They will appear here the moment someone submits the form.</p>
                  </div>
                ) : (
                  <div className="mt-8 space-y-4" data-testid="enquiries-list">
                    {enquiries.map((enq) => (
                      <div key={enq.id} className="rounded-3xl border border-white/10 bg-[#123328] p-6 sm:p-7" data-testid={`enquiry-card-${enq.id}`}>
                        <div className="flex flex-wrap items-start justify-between gap-3">
                          <div>
                            <p className="font-display text-lg font-bold text-[#F5F7F6]">{enq.full_name}</p>
                            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-[#EAB308]">
                              <Building2 className="h-3.5 w-3.5" /> {enq.practice_name}
                            </p>
                          </div>
                          <span className="font-mono text-[11px] text-[#A3B8B0]">
                            {new Date(enq.created_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
                          </span>
                        </div>
                        <div className="mt-4 flex flex-wrap gap-2">
                          <a href={`mailto:${enq.email}`} data-testid={`enquiry-email-${enq.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 font-mono text-[11px] text-[#F5F7F6] hover:bg-white/10 transition-colors">
                            <Mail className="h-3 w-3 text-[#EAB308]" /> {enq.email}
                          </a>
                          {enq.phone && (
                            <a href={`tel:${enq.phone}`} data-testid={`enquiry-phone-${enq.id}`} className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 font-mono text-[11px] text-[#F5F7F6] hover:bg-white/10 transition-colors">
                              <Phone className="h-3 w-3 text-[#EAB308]" /> {enq.phone}
                            </a>
                          )}
                          {enq.specialty && (
                            <span className="rounded-full bg-[#0A261E] px-3 py-1.5 font-mono text-[11px] text-[#A3B8B0]">{enq.specialty}</span>
                          )}
                          {enq.providers_needed && (
                            <span className="rounded-full bg-[#0A261E] px-3 py-1.5 font-mono text-[11px] text-[#A3B8B0]">{enq.providers_needed}</span>
                          )}
                        </div>
                        {enq.message && (
                          <p className="mt-4 rounded-2xl bg-[#0A261E] p-4 text-sm leading-relaxed text-[#F5F7F6]/90">{enq.message}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ) : (
          <div className="grid gap-8 lg:grid-cols-5">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="lg:col-span-2 rounded-3xl border border-white/10 bg-[#123328] p-7 sm:p-8"
              data-testid="studio-controls"
            >
              <h1 className="font-display text-2xl font-bold text-[#F5F7F6]">What are we marketing today?</h1>
              <p className="mt-2 text-sm text-[#A3B8B0]">
                On-brand content for US clinics, written by your AI assistant.
              </p>

              <div className="mt-7 space-y-2.5">
                <button
                  data-testid="studio-type-campaign_pack"
                  onClick={() => setContentType("campaign_pack")}
                  className={`flex w-full items-center justify-center gap-2 rounded-xl border-2 px-4 py-3.5 text-sm font-bold transition-all ${
                    contentType === "campaign_pack"
                      ? "border-[#EAB308] bg-[#EAB308] text-[#0A261E]"
                      : "border-[#EAB308]/40 bg-[#EAB308]/10 text-[#EAB308] hover:bg-[#EAB308]/20"
                  }`}
                >
                  <Layers className="h-4 w-4" />
                  All Platforms — Full Campaign Pack (One Click)
                </button>
                <p className="text-center font-mono text-[10px] text-[#A3B8B0]/70">
                  Every generation targets the USA only · or pick a single platform:
                </p>
              </div>
              <div className="mt-2.5 grid grid-cols-2 gap-2.5" data-testid="studio-content-types">
                {CONTENT_TYPES.filter((t) => t.id !== "campaign_pack").map((t) => {
                  const Icon = t.icon;
                  const active = contentType === t.id;
                  return (
                    <button
                      key={t.id}
                      data-testid={`studio-type-${t.id}`}
                      onClick={() => setContentType(t.id)}
                      className={`flex items-center gap-2 rounded-xl border px-3.5 py-3 text-left text-xs font-semibold transition-all ${
                        active
                          ? "border-[#EAB308] bg-[#EAB308]/15 text-[#EAB308]"
                          : "border-white/10 text-[#A3B8B0] hover:border-white/25 hover:text-[#F5F7F6]"
                      }`}
                    >
                      <Icon className="h-4 w-4 shrink-0" />
                      {t.label}
                    </button>
                  );
                })}
              </div>

              <div className="mt-6 space-y-1.5">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-[#F5F7F6]" htmlFor="studio-goal">
                    Campaign goal / focus
                  </label>
                  <button
                    type="button"
                    onClick={suggestGoal}
                    data-testid="studio-auto-goal-btn"
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#EAB308]/40 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-[#EAB308] transition-colors hover:bg-[#EAB308]/10"
                  >
                    <Wand2 className="h-3 w-3" /> Auto-Goal
                  </button>
                </div>
                <Textarea
                  id="studio-goal"
                  data-testid="studio-goal-input"
                  rows={4}
                  className="border-white/15 bg-[#0A261E] text-[#F5F7F6] placeholder:text-[#A3B8B0]/50"
                  placeholder="e.g. Promote our Full-Time Dedicated plan to pediatric clinics in Texas struggling with prior authorizations"
                  value={goal}
                  onChange={(e) => setGoal(e.target.value)}
                />
              </div>

              <div className="mt-5 space-y-1.5">
                <label className="text-xs font-semibold text-[#F5F7F6]">Tone</label>
                <div className="flex flex-wrap gap-2" data-testid="studio-tones">
                  {TONES.map((t) => (
                    <button
                      key={t}
                      data-testid={`studio-tone-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      onClick={() => setTone(t)}
                      className={`rounded-full px-3.5 py-1.5 font-mono text-[11px] transition-all ${
                        tone === t
                          ? "bg-[#F5F7F6] text-[#0A261E]"
                          : "border border-white/15 text-[#A3B8B0] hover:text-[#F5F7F6]"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={generate}
                disabled={generating}
                data-testid="studio-generate-btn"
                className="mt-8 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#EAB308] px-6 py-3.5 text-sm font-bold text-[#0A261E] transition-all hover:brightness-110 disabled:opacity-60"
              >
                {generating ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Writing…
                  </>
                ) : (
                  <>
                    <Sparkles className="h-4 w-4" /> Generate Marketing Content
                  </>
                )}
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="lg:col-span-3 flex flex-col gap-6"
            >
              <div className="flex-1 rounded-3xl border border-white/10 bg-[#123328] p-7 sm:p-8" data-testid="studio-output-panel">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A3B8B0]">Generated content</p>
                  {output && !generating && (
                    <button
                      onClick={() => copy(output)}
                      data-testid="studio-copy-btn"
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-semibold text-[#F5F7F6] hover:bg-white/10 transition-colors"
                    >
                      <Copy className="h-3.5 w-3.5" /> Copy
                    </button>
                  )}
                </div>
                <div
                  ref={outputRef}
                  className="mt-5 max-h-[420px] min-h-[280px] overflow-y-auto whitespace-pre-wrap rounded-2xl bg-[#0A261E] p-6 text-sm leading-relaxed text-[#F5F7F6]"
                  data-testid="studio-output"
                >
                  {output || (
                    <span className="text-[#A3B8B0]/60">
                      Your generated marketing content will appear here as it is written…
                    </span>
                  )}
                  {generating && <span className="ml-1 inline-block h-4 w-2 animate-pulse bg-[#EAB308]" />}
                </div>
              </div>

              {output && !generating && /linkedin/i.test(output) && /google ads/i.test(output) && (
                <LaunchKit output={output} />
              )}

              {history.length > 0 && (
                <div className="rounded-3xl border border-white/10 bg-[#123328] p-7" data-testid="studio-history">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#A3B8B0]">Recent generations</p>
                  <div className="mt-4 space-y-3">
                    {history.slice(0, 5).map((h) => (
                      <button
                        key={h.id}
                        data-testid={`studio-history-item-${h.id}`}
                        onClick={() => setOutput(h.content)}
                        className="flex w-full items-center justify-between gap-4 rounded-xl border border-white/10 px-4 py-3 text-left transition-colors hover:border-[#EAB308]/40"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-[#F5F7F6]">{h.goal}</p>
                          <p className="mt-0.5 font-mono text-[10px] text-[#A3B8B0]">
                            {CONTENT_TYPES.find((t) => t.id === h.content_type)?.label || h.content_type} · {h.tone} ·{" "}
                            {new Date(h.created_at).toLocaleDateString("en-US")}
                          </p>
                        </div>
                        <Copy className="h-3.5 w-3.5 shrink-0 text-[#A3B8B0]" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
